import type { ComponentPropsWithoutRef, JSX, ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { statusToneClasses, statusToneTextClass } from '@/lib/statusTone';
import { Icon, type IconName } from '@/components/ui/icon';
import type { EntityCardBadgeProps, EntityCardRow } from './entity-card-types';

export interface NavigableEntityCardProps extends Omit<
  ComponentPropsWithoutRef<'button'>,
  'title' | 'children' | 'id'
> {
  title: string;
  icon?: IconName;
  subtitle?: ReactNode;
  badge?: EntityCardBadgeProps;
  headerAside?: ReactNode;
  id?: string | null;
  idLabel?: string | null;
  rows?: EntityCardRow[];
  children?: ReactNode;
}

export function NavigableEntityCard({
  title,
  icon,
  subtitle,
  badge,
  headerAside,
  id,
  idLabel,
  rows,
  children,
  className,
  type,
  ...buttonProps
}: NavigableEntityCardProps): JSX.Element {
  const hasContent = Boolean((rows?.length ?? 0) > 0 || children);

  const badgeMarkup = badge ? (
    <span
      className={cn(
        'inline-flex max-w-[140px] flex-shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide',
        (() => {
          const tone = statusToneClasses(badge.tone);
          return cn(tone.subtleBackground, tone.subtleText);
        })()
      )}
      title={typeof badge.children === 'string' ? badge.children : undefined}
    >
      {badge.children}
    </span>
  ) : null;
  const headerRight =
    badgeMarkup || headerAside ? (
      <div className="flex items-start gap-2">
        {headerAside}
        {badgeMarkup}
      </div>
    ) : null;

  return (
    <button
      type={type ?? 'button'}
      className={cn(
        'group relative flex h-full w-full flex-col overflow-hidden rounded-lg border border-[var(--border-default)] bg-surface text-left shadow-sm transition-all duration-200',
        'hover:-translate-y-0.5 hover:shadow-md hover:border-brand-blue-300',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue-500/30 focus-visible:ring-offset-2',
        className
      )}
      {...buttonProps}
    >
      {/* Blue Navigation Indicator (Clean Strip) */}
      <div className="absolute bottom-0 left-0 top-0 w-1 bg-brand-blue-500 transition-all group-hover:w-1.5" />

      <div className="flex h-full flex-col gap-4 p-5 pl-6">
        {/* Header */}
        <div className="flex w-full items-start justify-between gap-3">
          <div className="flex items-start gap-3 overflow-hidden">
            {icon ? (
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-blue-50 text-brand-blue-600 transition-colors group-hover:bg-brand-blue-100 group-hover:text-brand-blue-700">
                <Icon name={icon} className="h-5 w-5" />
              </div>
            ) : null}
            <div className="flex min-w-0 flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <span className="truncate text-lg font-bold leading-tight text-text-primary transition-colors group-hover:text-brand-blue-700">
                  {title}
                </span>
              </div>
              {subtitle && <span className="truncate text-sm text-text-secondary">{subtitle}</span>}
              {id && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-text-secondary">
                  {idLabel && (
                    <span className="uppercase tracking-wider opacity-70">{idLabel}</span>
                  )}
                  <span className="rounded-[3px] bg-neutral-100 px-1.5 py-0.5 font-mono font-medium text-text-primary">
                    {id}
                  </span>
                </div>
              )}
            </div>
          </div>
          {headerRight}
        </div>

        {/* Separator if content exists */}
        {hasContent && <div className="h-px w-full bg-[var(--border-subtle)]" />}

        {hasContent ? (
          <div className="flex flex-1 flex-col justify-between gap-4">
            <div className="space-y-2.5 text-sm">
              {rows?.map((row, index) => (
                <div key={index} className="flex items-center justify-between gap-4">
                  <span className="text-text-secondary">{row.label}</span>
                  <span
                    className={cn(
                      'font-mono font-semibold tabular-nums',
                      statusToneTextClass(row.tone ?? 'neutral')
                    )}
                  >
                    {row.value}
                  </span>
                </div>
              ))}
              {children}
            </div>
          </div>
        ) : null}
      </div>
    </button>
  );
}
